import React from 'react';
import type { TrophyTier } from '../utils/trophyTier.ts';

interface TrophyBadgeProps {
  tier: TrophyTier;
  accuracy?: number;
  className?: string;
}

export const TrophyBadge: React.FC<TrophyBadgeProps> = ({
  tier,
  accuracy = 100,
  className = '',
}) => {
  if (tier === 'none') {
    return null;
  }

  // Label for screen readers
  const tierName = tier.charAt(0).toUpperCase() + tier.slice(1);
  const ariaLabel = `${tierName} trophy for scoring ${accuracy}%`;

  // Dynamic color palettes for the 3 tiers
  const palettes = {
    gold: {
      glow: 'rgba(245, 158, 11, 0.25)',
      sparkle: '#FDE047',
      cupGrad1: '#FDE047',
      cupGrad2: '#F59E0B',
      cupGrad3: '#D97706',
      cupGrad4: '#78350F',
      handleGrad1: '#FDE047',
      handleGrad2: '#D97706',
      starFill: '#FEF08A',
      starStroke: '#D97706',
      baseGrad1: '#FBBF24',
      baseGrad2: '#B45309',
    },
    silver: {
      glow: 'rgba(212, 212, 212, 0.22)',
      sparkle: '#FFFFFF',
      cupGrad1: '#FFFFFF',
      cupGrad2: '#E5E5E5',
      cupGrad3: '#A3A3A3',
      cupGrad4: '#525252',
      handleGrad1: '#FAFAFA',
      handleGrad2: '#737373',
      starFill: '#FFFFFF',
      starStroke: '#737373',
      baseGrad1: '#E5E5E5',
      baseGrad2: '#525252',
    },
    bronze: {
      glow: 'rgba(180, 83, 9, 0.25)',
      sparkle: '#FDBA74',
      cupGrad1: '#FDBA74',
      cupGrad2: '#CD7F32',
      cupGrad3: '#B45309',
      cupGrad4: '#78350F',
      handleGrad1: '#FDBA74',
      handleGrad2: '#92400E',
      starFill: '#FED7AA',
      starStroke: '#78350F',
      baseGrad1: '#CD7F32',
      baseGrad2: '#78350F',
    },
  }[tier];

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={`relative flex items-center justify-center select-none animate-in fade-in zoom-in-95 duration-400 motion-reduce:animate-none ${className}`}
    >
      <svg
        viewBox="0 0 200 180"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto max-w-[170px] sm:max-w-[190px] drop-shadow-2xl"
      >
        <defs>
          {/* Ambient Glow */}
          <radialGradient
            id={`trophy-glow-${tier}`}
            cx="50%"
            cy="50%"
            r="50%"
            fx="50%"
            fy="50%"
          >
            <stop offset="0%" stopColor={palettes.glow} />
            <stop offset="100%" stopColor={palettes.glow} stopOpacity="0" />
          </radialGradient>

          {/* Trophy Cup Gradient */}
          <linearGradient
            id={`cup-grad-${tier}`}
            x1="80"
            y1="35"
            x2="150"
            y2="115"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={palettes.cupGrad1} />
            <stop offset="45%" stopColor={palettes.cupGrad2} />
            <stop offset="85%" stopColor={palettes.cupGrad3} />
            <stop offset="100%" stopColor={palettes.cupGrad4} />
          </linearGradient>

          {/* Handles Gradient */}
          <linearGradient
            id={`handle-grad-${tier}`}
            x1="60"
            y1="45"
            x2="160"
            y2="90"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={palettes.handleGrad1} />
            <stop offset="100%" stopColor={palettes.handleGrad2} />
          </linearGradient>

          {/* Trophy Base Gradient */}
          <linearGradient
            id={`base-grad-${tier}`}
            x1="90"
            y1="110"
            x2="140"
            y2="150"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={palettes.baseGrad1} />
            <stop offset="100%" stopColor={palettes.baseGrad2} />
          </linearGradient>

          {/* Rim Gradient */}
          <linearGradient
            id={`rim-grad-${tier}`}
            x1="80"
            y1="35"
            x2="150"
            y2="35"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={palettes.cupGrad1} />
            <stop offset="50%" stopColor={palettes.cupGrad2} />
            <stop offset="100%" stopColor={palettes.cupGrad4} />
          </linearGradient>

          {/* Bible Cover Gradient */}
          <linearGradient
            id="bible-cover"
            x1="30"
            y1="90"
            x2="85"
            y2="165"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#2A2421" />
            <stop offset="50%" stopColor="#1E1917" />
            <stop offset="100%" stopColor="#141110" />
          </linearGradient>

          {/* Bible Page Edges Gradient */}
          <linearGradient
            id="bible-pages"
            x1="45"
            y1="130"
            x2="90"
            y2="165"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#E6D5B8" />
            <stop offset="50%" stopColor="#D4BE98" />
            <stop offset="100%" stopColor="#B89F74" />
          </linearGradient>

          {/* Gold Cross on Bible */}
          <linearGradient
            id="bible-cross"
            x1="45"
            y1="105"
            x2="65"
            y2="135"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#FDE047" />
            <stop offset="60%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#B45309" />
          </linearGradient>

          {/* Orange Ribbon Bookmark */}
          <linearGradient
            id="bible-ribbon"
            x1="52"
            y1="140"
            x2="60"
            y2="175"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#FF8A00" />
            <stop offset="100%" stopColor="#EA580C" />
          </linearGradient>

          {/* Specular highlight */}
          <linearGradient
            id="cup-sheen"
            x1="90"
            y1="40"
            x2="105"
            y2="95"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Ambient Warm Glow in Background */}
        <circle cx="115" cy="85" r="75" fill={`url(#trophy-glow-${tier})`} />

        {/* Sparkle rays in top right */}
        <g className="sparkles">
          {/* Ray 1 (Large top-right) */}
          <path
            d="M 162 26 L 165 32 L 171 35 L 165 38 L 162 44 L 159 38 L 153 35 L 159 32 Z"
            fill={palettes.sparkle}
            opacity="0.95"
          />
          {/* Ray 2 (Small upper) */}
          <path
            d="M 174 16 L 176 20 L 180 22 L 176 24 L 174 28 L 172 24 L 168 22 L 172 20 Z"
            fill={palettes.sparkle}
            opacity="0.8"
          />
          {/* Ray 3 (Medium accent) */}
          <path
            d="M 148 18 L 150 22 L 154 24 L 150 26 L 148 30 L 146 26 L 142 24 L 146 22 Z"
            fill={palettes.sparkle}
            opacity="0.85"
          />
        </g>

        {/* --- TROPHY --- */}
        {/* Handles (Left and Right) */}
        {/* Left Handle */}
        <path
          d="M 88 50 C 65 50, 56 65, 60 84 C 64 99, 78 102, 92 98 L 94 92 C 84 94, 73 92, 70 81 C 68 70, 75 58, 90 58 Z"
          fill={`url(#handle-grad-${tier})`}
        />
        {/* Right Handle */}
        <path
          d="M 142 50 C 165 50, 174 65, 170 84 C 166 99, 152 102, 138 98 L 136 92 C 146 94, 157 92, 160 81 C 162 70, 155 58, 140 58 Z"
          fill={`url(#handle-grad-${tier})`}
        />

        {/* Trophy Base Pedestal & Foot */}
        {/* Lower wide base plate */}
        <ellipse cx="115" cy="144" rx="36" ry="9" fill={`url(#base-grad-${tier})`} />
        {/* Stepped mid base */}
        <path
          d="M 92 138 C 92 134, 138 134, 138 138 L 134 144 C 134 144, 96 144, 96 144 Z"
          fill={`url(#base-grad-${tier})`}
        />
        <ellipse cx="115" cy="138" rx="23" ry="5" fill={`url(#cup-grad-${tier})`} />
        {/* Stem connecting base to cup */}
        <path
          d="M 108 106 L 122 106 L 120 134 L 110 134 Z"
          fill={`url(#cup-grad-${tier})`}
        />
        <ellipse cx="115" cy="116" rx="8" ry="3" fill={`url(#handle-grad-${tier})`} />

        {/* Main Cup Body */}
        <path
          d="M 82 40 C 82 78, 95 106, 115 106 C 135 106, 148 78, 148 40 Z"
          fill={`url(#cup-grad-${tier})`}
        />

        {/* Cup Inner / Lip rim */}
        <ellipse
          cx="115"
          cy="40"
          rx="33"
          ry="7"
          fill={`url(#rim-grad-${tier})`}
        />
        <ellipse
          cx="115"
          cy="40"
          rx="29"
          ry="5.5"
          fill={`url(#cup-grad-${tier})`}
          opacity="0.4"
        />

        {/* Soft Sheen Highlight down the left curve of the cup */}
        <path
          d="M 87 44 C 88 68, 97 92, 110 98 C 104 92, 96 74, 94 48 Z"
          fill="url(#cup-sheen)"
        />

        {/* Star in center of Trophy Cup */}
        <polygon
          points="115,59 118,67 127,67 120,72 123,80 115,75 107,80 110,72 103,67 112,67"
          fill={palettes.starFill}
          stroke={palettes.starStroke}
          strokeWidth="0.8"
          strokeLinejoin="round"
          className="drop-shadow-sm"
        />

        {/* --- HOLY BIBLE (resting at angle on lower left) --- */}
        <g className="bible-illustration">
          {/* Bible Shadow */}
          <ellipse
            cx="60"
            cy="158"
            rx="32"
            ry="7"
            fill="#050505"
            opacity="0.6"
          />

          {/* Bible Ribbon Hanging Out at Bottom */}
          <path
            d="M 52 142 Q 54 156, 56 166 L 61 161 L 66 167 Q 63 154, 60 142 Z"
            fill="url(#bible-ribbon)"
          />

          {/* Bible Pages Edge (Gilded ivory/golden pages block) */}
          <path
            d="M 44 140 L 78 153 L 86 142 L 52 129 Z"
            fill="url(#bible-pages)"
            stroke="#9C8259"
            strokeWidth="0.5"
          />
          {/* Fine lines on pages */}
          <path
            d="M 46 142 L 80 155 M 48 144 L 82 157"
            stroke="#7B6543"
            strokeWidth="0.5"
            opacity="0.4"
          />

          {/* Bible Cover Spine Curve */}
          <path
            d="M 28 126 C 26 130, 27 136, 32 142 L 40 146 C 36 140, 35 134, 36 129 Z"
            fill="#1E1917"
          />

          {/* Bible Front Cover (Tilted Perspective) */}
          <path
            d="M 36 128 L 74 143 L 64 100 L 26 86 Z"
            fill="url(#bible-cover)"
            stroke="#3F3733"
            strokeWidth="0.8"
          />

          {/* Subtle Cover Margin Emboss Line */}
          <path
            d="M 38 124 L 70 137 L 62 104 L 30 92 Z"
            stroke="#38302C"
            strokeWidth="0.6"
            fill="none"
          />

          {/* Gold Cross on Cover */}
          {/* Vertical beam */}
          <rect
            x="48"
            y="98"
            width="5"
            height="22"
            rx="1"
            transform="rotate(22 50 109)"
            fill="url(#bible-cross)"
          />
          {/* Horizontal beam */}
          <rect
            x="42"
            y="105"
            width="15"
            height="4.5"
            rx="1"
            transform="rotate(22 49 107)"
            fill="url(#bible-cross)"
          />
        </g>
      </svg>
    </div>
  );
};
